import { createTheme, alpha, type ThemeOptions } from '@mui/material/styles';

/**
 * MyBooking Medical theme.
 *
 * Design tenets (matters more than brand colors):
 *   1. Calm. Clinical software gets stared at for 10 hours a day.
 *      No saturated alerts; reserve red for actual contraindications.
 *   2. Legible. Inter @ 14–15 px base, 1.55 line-height in body.
 *      Numbers use tabular figures so dosage tables align.
 *   3. Status-aware. Severity scale matches DDI severity:
 *      'minor' (info) → 'moderate' (warning) → 'major'/'contra' (error).
 *   4. Accessible by default. Contrast ratios validated for AA on text;
 *      AAA on dosage / vitals readouts.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Color palette
// ─────────────────────────────────────────────────────────────────────────────

const palette = {
  primary: {
    main:  '#0b5e8e',
    light: '#3e89bc',
    dark:  '#003862',
    contrastText: '#ffffff',
  },
  secondary: {
    main:  '#0d8a73',
    light: '#5fbba2',
    dark:  '#005d47',
    contrastText: '#ffffff',
  },
  // DDI severity colors — used by Rx composer and by triage badges.
  severity: {
    none:     '#6e7780',
    minor:    '#3e89bc',  // info
    moderate: '#b58105',  // warning
    major:    '#c14d3c',  // error (less aggressive than #d32f2f)
    contra:   '#8c1d10',  // contraindication — distinctly darker
  },
  triage: {
    routine:   '#0d8a73',
    soon:      '#3e89bc',
    urgent:    '#b58105',
    emergency: '#8c1d10',
  },
  background: {
    default: '#f5f7fa',
    paper:   '#ffffff',
    subtle:  '#eef2f6',
  },
  text: {
    primary:   '#10212f',
    secondary: '#516373',
    disabled:  '#9aa6b2',
  },
  divider: '#e1e6ec',
  success: { main: '#0d8a73' },
  warning: { main: '#b58105' },
  error:   { main: '#c14d3c' },
  info:    { main: '#3e89bc' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Theme
// ─────────────────────────────────────────────────────────────────────────────

const themeOptions: ThemeOptions = {
  palette,
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 14,
    h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.015em' },
    h3: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.25rem', fontWeight: 600 },
    h5: { fontSize: '1.125rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    subtitle1: { fontSize: '1rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55, color: palette.text.secondary },
    button: { textTransform: 'none', fontWeight: 600 },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em' },
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 2px rgba(16,33,47,0.04)',
    '0 1px 3px rgba(16,33,47,0.06), 0 1px 2px rgba(16,33,47,0.04)',
    '0 2px 4px rgba(16,33,47,0.08)',
    '0 4px 8px rgba(16,33,47,0.08)',
    '0 6px 12px rgba(16,33,47,0.10)',
    '0 8px 16px rgba(16,33,47,0.10)',
    ...Array<string>(18).fill('0 12px 24px rgba(16,33,47,0.12)'),
  ] as ThemeOptions['shadows'],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
        },
        // Tabular figures for clinical numbers
        '.tabular': { fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'inherit', elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          borderBottom: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingInline: 16,
          paddingBlock: 8,
        },
        contained: { boxShadow: 'none' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${palette.divider}`,
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, alignItems: 'center' },
        standardError:   { backgroundColor: alpha(palette.error.main, 0.08) },
        standardWarning: { backgroundColor: alpha(palette.warning.main, 0.10) },
        standardInfo:    { backgroundColor: alpha(palette.info.main, 0.08) },
        standardSuccess: { backgroundColor: alpha(palette.success.main, 0.10) },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiTableHead: {
      styleOverrides: {
        root: { backgroundColor: palette.background.subtle },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
  },
};

export const theme = createTheme(themeOptions);

// Severity → MUI alert severity mapper, used everywhere DDI / regulation
// engine results render.
export type ClinicalSeverity = 'none' | 'minor' | 'moderate' | 'major' | 'contra';

export const severityToAlertSeverity = (s: ClinicalSeverity): 'success' | 'info' | 'warning' | 'error' => {
  switch (s) {
    case 'none':     return 'success';
    case 'minor':    return 'info';
    case 'moderate': return 'warning';
    case 'major':    return 'error';
    case 'contra':   return 'error';
  }
};

export const severityColor = (s: ClinicalSeverity): string => palette.severity[s];

export type TriageLevel = 'routine' | 'soon' | 'urgent' | 'emergency';
export const triageColor = (t: TriageLevel): string => palette.triage[t];
